import React from 'react';
import styled from 'styled-components';
import { Check } from 'lucide-react';
import { EVENT_LAYOUTS } from 'helpers/eventSessions';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

/*
 * Cores da página pública (e não do painel): a prévia mostra o que o comprador
 * vai ver, que usa o roxo do evento.
 */
const ACCENT = colors.occasionalPurple;
const SOFT = '#f0e7fc';
const HERO = '#e9dcfb';
const LINE = '#e6e6ee';
const BAR = '#dcdce6';
const MUTED = '#9a9aaa';
const INK = '#2a2a3a';

function Bar({ x, y, w, h = 4, fill = BAR }) {
    return <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={fill} />;
}

function Panel({ x, y, w, h, rx = 6, fill = '#fff' }) {
    return <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill} stroke={LINE} />;
}

/** Número que liga um ponto da miniatura à legenda do balão. */
function Mark({ n, x, y }) {
    return (
        <g>
            <circle cx={x} cy={y} r='6.5' fill={INK} stroke='#fff' strokeWidth='1.5' />
            <text x={x} y={y} fill='#fff' fontSize='8' fontWeight='700' textAnchor='middle' dominantBaseline='central'>
                {n}
            </text>
        </g>
    );
}

/** Capa do evento no topo, igual nos dois layouts. */
function Frame({ children }) {
    return (
        <>
            <rect x='0.5' y='0.5' width='319' height='207' rx='10' fill='#fff' stroke={LINE} />
            <path d='M1 10a9 9 0 0 1 9-9h300a9 9 0 0 1 9 9v34H1z' fill={HERO} />
            <Bar x={14} y={16} w={120} h={9} fill={INK} />
            <Bar x={14} y={31} w={84} h={5} fill={ACCENT} />
            {children}
        </>
    );
}

/** Linha de ingresso do cartão da direita. */
function TicketRow({ y }) {
    return (
        <>
            <rect x='222' y={y} width='76' height='26' rx='5' fill='#f7f5fd' />
            <Bar x={228} y={y + 6} w={34} fill={MUTED} />
            <Bar x={228} y={y + 15} w={22} h={5} fill={INK} />
            <circle cx='284' cy={y + 13} r='5' fill='#fff' stroke={ACCENT} />
            <Bar x={281.5} y={y + 12.5} w={5} h={1} fill={ACCENT} />
        </>
    );
}

function DefaultWireframe({ marks }) {
    return (
        <Frame>
            <Panel x={12} y={54} w={188} h={70} />
            <Bar x={22} y={63} w={44} h={6} fill={ACCENT} />
            <Bar x={22} y={76} w={168} />
            <Bar x={22} y={85} w={160} />
            <Bar x={22} y={94} w={168} />
            <Bar x={22} y={103} w={110} />

            <Panel x={12} y={130} w={188} h={34} />
            <Bar x={22} y={138} w={58} h={5} fill={ACCENT} />
            <rect x='22' y='148' width='34' height='10' rx='5' fill={SOFT} />
            <rect x='60' y='148' width='40' height='10' rx='5' fill={SOFT} />
            <rect x='104' y='148' width='30' height='10' rx='5' fill={SOFT} />

            <Panel x={12} y={170} w={188} h={30} />
            <Bar x={22} y={178} w={70} h={5} fill={ACCENT} />
            <Bar x={22} y={188} w={150} />

            <Panel x={212} y={54} w={96} h={106} rx={8} />
            <Bar x={222} y={64} w={50} h={6} fill={ACCENT} />
            <TicketRow y={78} />
            <TicketRow y={108} />
            <rect x='222' y='142' width='76' height='12' rx='6' fill={ACCENT} />
            <Bar x={248} y={146} w={24} h={4} fill='#fff' />

            {marks && (
                <>
                    <Mark n={1} x={198} y={57} />
                    <Mark n={2} x={306} y={57} />
                </>
            )}
        </Frame>
    );
}

const SESSIONS = [
    { y: 120, done: false },
    { y: 148, done: false },
    { y: 176, done: true },
];

function ScheduleWireframe({ marks }) {
    return (
        <Frame>
            {/* abas: Programação (ativa), Detalhes e Social */}
            <rect x='12' y='67' width='188' height='1.5' fill={LINE} />
            <rect x='12' y='66.5' width='62.6' height='2.5' fill={ACCENT} />
            <Bar x={22} y={58} w={42} fill={ACCENT} />
            <Bar x={88} y={58} w={36} fill={MUTED} />
            <Bar x={156} y={58} w={26} fill={MUTED} />

            {/* um botão por dia do evento */}
            {[12, 77, 142].map((x, index) => (
                <g key={x}>
                    <rect
                        x={x}
                        y='76'
                        width='58'
                        height='22'
                        rx='6'
                        fill={index === 0 ? ACCENT : '#fff'}
                        stroke={index === 0 ? ACCENT : LINE}
                    />
                    <Bar x={x + 21} y={81} w={16} h={3} fill={index === 0 ? '#fff' : MUTED} />
                    <Bar x={x + 18} y={88} w={22} h={5} fill={index === 0 ? '#fff' : INK} />
                </g>
            ))}

            {/* filtros por tipo */}
            <rect x='12' y='104' width='24' height='10' rx='5' fill={SOFT} stroke={ACCENT} />
            <rect x='40' y='104' width='30' height='10' rx='5' fill='#fff' stroke={LINE} />
            <rect x='74' y='104' width='34' height='10' rx='5' fill='#fff' stroke={LINE} />
            <rect x='112' y='104' width='28' height='10' rx='5' fill='#fff' stroke={LINE} />

            {/* sessões: horário, tipo, título, palestrante e o botão de presença */}
            {SESSIONS.map(({ y, done }) => (
                <g key={y}>
                    <Panel x={12} y={y} w={188} h={24} rx={5} />
                    <Bar x={18} y={y + 6} w={20} h={5} fill={INK} />
                    <Bar x={18} y={y + 14} w={14} h={3} fill={MUTED} />
                    <rect x='46' y={y + 5} width='22' height='5' rx='2.5' fill={SOFT} />
                    <Bar x={46} y={y + 12} w={76} fill={INK} />
                    <circle cx='48.5' cy={y + 19.5} r='2.5' fill={BAR} />
                    <Bar x={54} y={y + 18} w={30} h={3} fill={MUTED} />
                    <rect
                        x='140'
                        y={y + 6}
                        width='54'
                        height='12'
                        rx='4'
                        fill={done ? '#fff' : ACCENT}
                        stroke={ACCENT}
                        strokeWidth={done ? 1.2 : 0}
                    />
                    <Bar x={154} y={y + 10} w={26} h={4} fill={done ? ACCENT : '#fff'} />
                </g>
            ))}

            {/* carrinho: ingresso do evento e as presenças, num pedido só */}
            <Panel x={212} y={54} w={96} h={126} rx={8} />
            <Bar x={222} y={64} w={50} h={6} fill={ACCENT} />
            <TicketRow y={78} />
            <Bar x={222} y={112} w={54} h={4} fill={ACCENT} />
            <circle cx='225' cy='125' r='2.5' fill={SOFT} stroke={ACCENT} />
            <Bar x={232} y={123} w={60} h={4} />
            <circle cx='225' cy='134' r='2.5' fill={SOFT} stroke={ACCENT} />
            <Bar x={232} y={132} w={50} h={4} />
            <Bar x={222} y={146} w={26} h={4} fill={MUTED} />
            <Bar x={274} y={145} w={24} h={5} fill={INK} />
            <rect x='222' y='160' width='76' height='12' rx='6' fill={ACCENT} />
            <Bar x={248} y={164} w={24} h={4} fill='#fff' />

            {marks && (
                <>
                    <Mark n={1} x={73} y={57} />
                    <Mark n={2} x={199} y={76} />
                    <Mark n={3} x={193} y={126} />
                    <Mark n={4} x={306} y={57} />
                </>
            )}
        </Frame>
    );
}

function Wireframe({ layout, marks = false }) {
    return (
        <Svg viewBox='0 0 320 208' aria-hidden='true' focusable='false'>
            {layout === EVENT_LAYOUTS.SCHEDULE ? <ScheduleWireframe marks={marks} /> : <DefaultWireframe marks={marks} />}
        </Svg>
    );
}

/**
 * Cartão de escolha de layout. Mostra uma miniatura da página na própria carta
 * (vale também no toque, onde não existe passar o mouse) e, ao passar o mouse ou
 * focar com o teclado, abre a prévia ampliada com o que muda em cada ponto.
 */
export default function LayoutOption({ item, selected, disabled, onSelect, alignEnd = false }) {
    const tooltipId = `layout-preview-${item.value}`;

    return (
        <Option>
            <Card
                type='button'
                role='radio'
                aria-checked={selected}
                aria-describedby={tooltipId}
                $selected={selected}
                disabled={disabled}
                onClick={() => onSelect(item.value)}
            >
                <Thumb>
                    <Wireframe layout={item.value} />
                </Thumb>
                <Copy>
                    <Title>
                        {item.title}
                        {selected && <Check size={16} strokeWidth={3} />}
                    </Title>
                    <Text>{item.description}</Text>
                </Copy>
            </Card>

            <Popover id={tooltipId} role='tooltip' $end={alignEnd}>
                <PopoverLabel>Prévia da página pública</PopoverLabel>
                <Wireframe layout={item.value} marks />
                <Legend>
                    {item.notes.map((note, index) => (
                        <li key={note}>
                            <Badge>{index + 1}</Badge>
                            <span>{note}</span>
                        </li>
                    ))}
                </Legend>
            </Popover>
        </Option>
    );
}

const Svg = styled.svg`
    display: block;
    width: 100%;
    height: auto;
`;

const Option = styled.div`
    position: relative;
    display: flex;
`;

const Card = styled.button`
    display: flex;
    flex: 1;
    align-items: center;
    gap: 14px;
    min-width: 0;
    text-align: left;
    padding: 14px;
    border-radius: 12px;
    cursor: pointer;
    font-family: inherit;
    background: ${({ $selected }) => ($selected ? panelAccentAlpha(colors.occasionalPurple, '0D') : colors.neutralLight)};
    border: 2px solid ${({ $selected }) => ($selected ? panelAccent(colors.occasionalPurple) : `${colors.night}22`)};

    &:disabled { opacity: 0.6; cursor: wait; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const Thumb = styled.span`
    flex: 0 0 116px;
    width: 116px;
    line-height: 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 4px ${colors.night}33;
`;

const Copy = styled.span`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const Title = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Text = styled.span`
    font-size: ${fonts.sizes.small};
    line-height: 1.5;
    color: ${colors.night};
`;

/* Só onde há mouse; no toque a miniatura da carta já mostra a página. */
const Popover = styled.div`
    display: none;

    @media (hover: hover) {
        display: block;
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        z-index: 30;
        width: 100%;
        padding: 14px;
        border-radius: 14px;
        background: ${colors.neutralLight};
        border: 1px solid ${colors.night}22;
        box-shadow: 0 12px 32px rgba(20, 16, 48, 0.18);
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-4px);
        transition: opacity 0.15s ease, transform 0.15s ease, visibility 0s linear 0.15s;

        ${Option}:hover &,
        ${Option}:focus-within & {
            opacity: 1;
            visibility: visible;
            transform: none;
            transition-delay: 0.12s, 0.12s, 0.12s;
        }

        @media screen and (min-width: ${breakpoints.md}) {
            width: 440px;
            ${({ $end }) => ($end ? 'left: auto; right: 0;' : '')}
        }
    }
`;

const PopoverLabel = styled.div`
    margin-bottom: 8px;
    font-size: ${fonts.sizes.mini};
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Legend = styled.ol`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
    font-size: ${fonts.sizes.small};
    line-height: 1.4;
    color: ${colors.nightDark};

    li {
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }
`;

const Badge = styled.span`
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${INK};
`;
